/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// React
import { useState } from 'react';

// Mantine
import { Box, Title, Stack } from '@mantine/core';

// Components
import { Select } from '@components/atoms/Select';
import { Button } from '@components/atoms/Button';
import { Text } from '@components/atoms/Text';

// Styles
import classes from './UpdateMeterPointsForm.module.css';

// Utils
import { convertArrayToLabelValue } from '@utils/functions';

// Icons
import useStore from '@store/index';

// I18n
import { useTranslation } from 'react-i18next';
import { useSites } from 'shared/api/get-sites';
import { useMeasurabilities } from 'shared/api/get-measurabilities';
import SingleFormMeterPoint from './SingleFormMeterPoint';
import { useMediaQuery } from '@mantine/hooks';
import { Divider } from '@components/atoms';
import { useProducers } from 'shared/api/get-producers';
import { useUpdateMeterPoint } from '@features/resources/meter-points/api/update-meter-point';

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
export const UpdateMeterPointsForm = (props: {
	form: any;
	onCancel: () => void;
	onBack: () => void;
	isPreviousMeasurabilityProjectDetails: boolean;
}) => {
	const [meterPointsProducer, setMeterPointsProducer] =
		useState('');

	const { meterPointsPage, meterPointsPageSize } =
		useStore();

	const { t } = useTranslation();

	const { data: projects } = useMeasurabilities({
		entity: meterPointsProducer
			? meterPointsProducer
			: [],
	});
	const { data: sites } = useSites();

	const { data: producersToAddProductions } =
		useProducers();

	const updateMeterPoint = useUpdateMeterPoint({
		page: meterPointsPage,
		size: meterPointsPageSize,
		mutationConfig: {
			onSuccess() {
				props.onBack();
			},
		},
	});

	const isBigScreen = useMediaQuery('(min-width: 120em)');

	const getForm = () => {
		/* ******************************** HELPERS *********************************** */

		const handleSubmitForm = () => {
			props.form.validate();
			if (props.form.isValid()) {
				const { site, meterPoints, id } =
					props.form.values;

				const body = { site, ...meterPoints[0] };

				updateMeterPoint.mutate({
					id,
					body: {
						...(props.form.isDirty(
							'meterPoints.0.serialNumber'
						)
							? {
									serialNumber: body.serialNumber,
								}
							: {}),
						...(props.form.isDirty(
							'meterPoints.0.location.countryCode'
						) ||
						props.form.isDirty(
							'meterPoints.0.location.place'
						)
							? {
									location: {
										countryCode:
											body.location.countryCode,
										place: body.location.place,
									},
								}
							: {}),
					},
				});
			}
		};

		/* ******************************** CONSTANTS ******************************* */

		const requiredFields = [
			'organization',
			'entity',
			'site',
			'project',
			'startDate',
		];
		const isValidSelects = requiredFields.every((field) =>
			props.form.isValid(field)
		);
		const isEnableToAdd = isValidSelects;

		const { onChange: onChangeSite, ...entityProps } =
			props.form.getInputProps('entity');

		const handleChangeEntity = (value: string) => {
			if (value) setMeterPointsProducer(value);
			props.form.setFieldValue('entity', value);
			props.form.setFieldValue('project', null);
		};

		const {
			onChange: onChangeProject2,
			...projectProps
		} = props.form.getInputProps('project');

		const handleChangeProject = (value: string) => {
			props.form.setFieldValue('project', value);
			props.form.setFieldValue('site', null);
		};

		const { onChange: onChangeSite2, ...siteProps } =
			props.form.getInputProps('site');

		const handleChangeSite = (value: string) => {
			props.form.setFieldValue('site', value);
		};

		/* ******************************** RENDERING ******************************* */
		return (
			<form
				className={classes.containerForm}
				onSubmit={props.form.onSubmit(
					(values: any) => {}
				)}
			>
				<Box className={classes.containerTitle}>
					<Title className={classes.titleForm}>
						{t('global.meterPoints')}
					</Title>
					<Text className={classes.subTitleForm}>
						/ {t('meterPoints.updateMeterPoint')}
					</Text>
				</Box>

				{/* Row for Producer */}
				<Box className={classes.row}>
					<Box className={classes.containerInput}>
						<Select
							disabled
							size={isBigScreen ? 'md' : 'sm'}
							label={t('global.producer')}
							withAsterisk
							data={convertArrayToLabelValue(
								producersToAddProductions
							)}
							placeholder={t('global.selectEntity')}
							onChange={handleChangeEntity}
							{...entityProps}
						/>
					</Box>
				</Box>
				{/* Row for Project & Site */}
				<Box className={classes.row}>
					<Box className={classes.containerInput}>
						<Select
							disabled
							size={isBigScreen ? 'md' : 'sm'}
							label={t('global.project')}
							withAsterisk
							data={convertArrayToLabelValue(projects)}
							placeholder={t('global.selectProject')}
							onChange={handleChangeProject}
							{...projectProps}
						/>
					</Box>
					<Box className={classes.containerInput}>
						<Select
							disabled
							size={isBigScreen ? 'md' : 'sm'}
							label={t('global.site')}
							withAsterisk
							data={convertArrayToLabelValue(
								sites.filter((item: any) =>
									item.project
										? item.project.id ==
											props.form.getInputProps('project')
												.value
										: []
								)
							)}
							placeholder={t('global.selectSite')}
							onChange={handleChangeSite}
							{...siteProps}
						/>
					</Box>
				</Box>
				<Divider size={'xs'} />
				<Stack>
					{props.form
						.getValues()
						.meterPoints.map(
							(item: any, index: number) => (
								<SingleFormMeterPoint
									key={item.id}
									form={props.form}
									item={item}
									index={index}
								/>
							)
						)}
				</Stack>
				{/* Row for Buttons */}
				<Box className={classes.rowButtons}>
					<Box className={classes.containerButtonCancel}>
						<Button
							fullWidth
							size={isBigScreen ? 'lg' : 'md'}
							variant="transparent"
							onClick={() => {
								props.onCancel();
							}}
						>
							{t('global.cancel')}
						</Button>
					</Box>
					<Box className={classes.containerButton}>
						<Button
							data-autofocus
							disabled={!isEnableToAdd}
							fullWidth
							size={isBigScreen ? 'lg' : 'md'}
							onClick={handleSubmitForm}
							loading={updateMeterPoint.isPending}
						>
							{t('meterPoints.updateMeterPoint')}
						</Button>
					</Box>
				</Box>
			</form>
		);
	};

	/* ******************************** RENDERING ******************************* */
	return (
		<Box className={classes.container}>
			<Box className={classes.containerEmpty} />
			{getForm()}
			<Box className={classes.containerEmpty} />
		</Box>
	);
};
